import {
  DEFAULT_THEME,
  createTheme,
  defaultVariantColorsResolver,
  mergeMantineTheme,
  parseThemeColor,
} from '@mantine/core';
import { tokens } from './tokens';
import type { MantineTheme, VariantColorsResolverInput } from '@mantine/core';

/**
 * Custom variant color resolver for Mantine v8
 * Ensures WCAG AA contrast compliance for all button variants
 */
const variantColorResolver = (input: VariantColorsResolverInput) => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const parsedColor = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  });

  // Gradient variant — use darker shades for better contrast
  if (input.variant === 'gradient') {
    return {
      ...defaultResolvedColors,
      background: `linear-gradient(
        45deg,
        var(--mantine-color-${parsedColor.color}-6) 0%,
        var(--mantine-color-${parsedColor.color}-9) 100%
      )`,
      color: 'var(--mantine-color-white)',
    };
  }

  // Light and subtle variants — theme-aware text for light/dark mode.
  // Light mode: -9 (dark text on pale background). Dark mode: -1 (light text on dark background).
  const themeAwareFg = `light-dark(var(--mantine-color-${parsedColor.color}-9), var(--mantine-color-${parsedColor.color}-1))`;

  if (input.variant === 'subtle') {
    return {
      ...defaultResolvedColors,
      color: themeAwareFg,
      hoverColor: themeAwareFg,
    };
  }

  if (input.variant === 'light') {
    return {
      ...defaultResolvedColors,
      color: themeAwareFg,
      hoverColor: themeAwareFg,
    };
  }

  return defaultResolvedColors;
};


const themeOverride = createTheme({
  // 1. Colors
  colors: tokens.colors,
  primaryColor: 'primary',
  primaryShade: { light: 5, dark: 5 }, // Asset colors use index 5 for base
  variantColorResolver,

  // 1b. Layout consistency: components without explicit radius use this (Skeleton, Progress, etc.)
  defaultRadius: tokens.themeDefaults.defaultRadius,

  autoContrast: true,

  // Override luminance threshold for better contrast
  luminanceThreshold: 0.3,

  defaultGradient: {
    from: 'primary.1',
    to: 'primary.9',
    deg: 45,
  },

  white: '#FFFFFF',
  black: '#000000',

  // 2. Typography
  fontFamily: tokens.typography.fontFamily.sans,
  fontFamilyMonospace: tokens.typography.fontFamily.mono,
  headings: {
    fontFamily: tokens.typography.fontFamily.sans,
    fontWeight: tokens.typography.fontWeights.semibold,
    sizes: {
      h1: {
        fontSize: '2.5rem', // 40px - UX Spec: H1: 2.5rem (40px), line-height: 1.2, font-weight: 700
        lineHeight: tokens.typography.lineHeights.tight, // 1.2
        fontWeight: tokens.typography.fontWeights.bold, // 700
      },
      h2: {
        fontSize: '2rem', // 32px - UX Spec: H2: 2rem (32px), line-height: 1.2, font-weight: 600
        lineHeight: tokens.typography.lineHeights.tight, // 1.2
        fontWeight: tokens.typography.fontWeights.semibold, // 600
      },
      h3: {
        fontSize: '1.5rem', // 24px - UX Spec: H3: 1.5rem (24px), line-height: 1.2, font-weight: 600
        lineHeight: tokens.typography.lineHeights.tight, // 1.2
        fontWeight: tokens.typography.fontWeights.semibold, // 600
      },
      h4: {
        fontSize: tokens.typography.fontSizes.xl, // 1.25rem (20px) - UX Spec: H4: 1.25rem (20px), line-height: 1.2, font-weight: 600
        lineHeight: tokens.typography.lineHeights.tight, // 1.2 - UX Spec
        fontWeight: tokens.typography.fontWeights.semibold, // 600
      },
      h5: {
        fontSize: tokens.typography.fontSizes.lg,
        lineHeight: tokens.typography.lineHeights.normal,
        fontWeight: tokens.typography.fontWeights.medium,
      },
      h6: {
        fontSize: tokens.typography.fontSizes.md,
        lineHeight: tokens.typography.lineHeights.normal,
        fontWeight: tokens.typography.fontWeights.medium,
      },
    },
  },
  fontSizes: tokens.typography.fontSizes,

  // 3. Spacing
  spacing: tokens.spacing,

  // 4. Borders & Radius
  radius: tokens.radius,

  // 5. Shadows
  shadows: tokens.shadows,

  // 6. Z-Index
  other: {
    zIndex: tokens.zIndices,

    // 7. Breakpoints
    breakpoints: tokens.breakpoints,

    // 8. Transitions
    transition: tokens.transitions,

    // 9. Layout
    container: tokens.containers,
    mobileFooterHeight: tokens.layout.mobileFooterHeight,

    // 10. Aspect Ratios
    aspectRatio: tokens.aspectRatios,

    // 11. Dark Mode (handled by Mantine's dark mode system)
    darkMode: {
      textContrast: 0.9,
      backgroundContrast: 0.85,
    },

    // 12. Internationalization
    dir: 'ltr',
    textDir: 'ltr',

    // 13. Accessibility
    focusRing: '0 0 0 2px var(--mantine-color-primary-4)',
    focusRingOffset: '2px',
  },

  // 14. Component Theming
  components: {
    // Root layout styling
    AppShell: {
      defaultProps: {
        padding: 0,
      },
      styles: (theme: MantineTheme) => ({
        main: {
          background: theme.colors.body[6], // Uses virtualColor body - adapts to light/dark (22% in dark mode)
          width: '100%',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        },
      }),
    },

    // Button variants - Mantine v8 with WCAG AA contrast
    Button: {
      defaultProps: {
        radius: tokens.radius.xl,
        size: 'sm',
        color: 'primary',
        variant: 'default',
      },
      styles: {
        root: {
          fontWeight: tokens.typography.fontWeights.medium,
          letterSpacing: tokens.typography.letterSpacing.normal,
          boxShadow: tokens.shadows.lg,
          paddingInline: tokens.spacing.md,
          paddingBlock: tokens.spacing.xs,
          borderRadius: tokens.radius.xl,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
      vars: (_theme: MantineTheme, props: Record<string, unknown>) => {
        // Use Mantine v8 CSS variables for dynamic styling
        if (props.variant === 'default') {
          return {
            root: {
              // Use light-dark() for proper contrast in both themes
              '--button-bg': 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))',
              '--button-color': 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-1))',
              '--button-bd': 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
              '--button-hover': 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-5))',
            },
          };
        }

      if (props.variant === 'subtle' || props.variant === 'light') {
        return {
          root: {
            // Use light-dark() for proper contrast in both themes
            // Light mode: gray-9 (dark text), Dark mode: gray-1 (light text)
            '--button-color': 'light-dark(var(--mantine-color-gray-9), var(--mantine-color-gray-1))',
            '--button-hover': 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-gray-8))',
          },
        };
      }

        return { root: {} };
      },
    },

    // Anchor (links) - ensure they're distinguishable without color (WCAG)
    Anchor: {
      defaultProps: {
        underline: 'always', // Always underline links for accessibility
      },
      styles: {
        root: {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
      vars: (_theme: MantineTheme, props: Record<string, unknown>) => {
        // Use light-dark() CSS function for automatic theme adaptation
        // Light mode: darker shade (8) for contrast on light background
        // Dark mode: lighter shade (3) for contrast on dark background

        // Small text needs adjusted colors for WCAG AA compliance
        if (props.size === 'xs' || props.size === 'sm') {
          return {
            root: {
              '--anchor-color': 'light-dark(var(--mantine-color-primary-8), var(--mantine-color-primary-3))',
            },
          };
        }
        // Default size also needs proper contrast
        return {
          root: {
            '--anchor-color': 'light-dark(var(--mantine-color-primary-7), var(--mantine-color-primary-4))',
          },
        };
      },
    },

    // Badge - ensure good contrast
    Badge: {
      defaultProps: {
        radius: tokens.radius.xl,
        size: 'sm',
        variant: 'filled', // Filled variant has better contrast
      },
    },

    ActionIcon: {
      defaultProps: {
        radius: tokens.radius.xl,
        variant: 'light',
      },
      styles: {
        root: {
          boxShadow: tokens.shadows.md,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },

    // ThemeIcon - match ActionIcon radius for consistent icon containers
    ThemeIcon: {
      defaultProps: {
        radius: 'xl',
      },
    },

    // Menu / dropdowns - consistent shadow
    Menu: {
      defaultProps: {
        shadow: 'md',
      },
    },

    // UnstyledButton - used in Header; ensure reliable touch/click on WebKit (iOS Chrome/Safari)
    UnstyledButton: {
      styles: {
        root: {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },

    // Input styling
    Input: {
      defaultProps: {
        radius: tokens.radius.md,
        size: 'sm',
      },
      styles: () => ({
        input: {
          boxShadow: tokens.shadows.formInput,
          border: 'none',
          // 16px prevents iOS Safari zoom on input focus. Safari zooms when font-size <= 15px (our
          // size="sm" uses 0.875rem/14px). See: https://css-tricks.com/16px-or-larger-text-prevents-ios-form-zoom/
          fontSize: '1rem',
          WebkitTapHighlightColor: 'transparent',
          '&:focus': {
            borderColor: 'var(--mantine-color-primary-5)',
          },
        },
      }),
    },

    // Card styling
    Card: {
      defaultProps: {
        radius: tokens.radius.lg,
        shadow: tokens.shadows.sm,
        withBorder: true,
      },
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: theme.colors.surface[5], // surface[5]: light=~white, dark=32% — elevated vs body for clear contrast
          borderColor:
            'light-dark(var(--mantine-color-neutral-3), var(--mantine-color-darkSurface-5))',
          transition: `${tokens.transitions.default}, transform ${tokens.transitions.default}`,
          /* Hover (lift) is applied only for (hover: hover) in __root.css to avoid WebKit "sticky hover" on first tap. */
        },
      }),
    },

    Paper: {
      defaultProps: {
        radius: tokens.radius.xl,
        shadow: tokens.shadows.xl,
        withBorder: true,
      },
      styles: (theme: MantineTheme) => ({
        root: {
          backgroundColor: theme.colors.surface[4], // surface[4]: light=~white, dark=30% — elevated vs body (22%) for dark mode contrast
          borderColor:
            'light-dark(var(--mantine-color-neutral-3), var(--mantine-color-darkSurface-5))',
        },
      }),
    },

    // Modal styling
    Modal: {
      defaultProps: {
        radius: tokens.radius.md,
        shadow: tokens.shadows.xl,
        overlayProps: {
          blur: 3,
          backgroundOpacity: 0.55,
        },
      },
      styles: (theme: MantineTheme) => ({
        content: {
          backgroundColor: theme.colors.body[6], // Uses virtualColor body - adapts to light/dark (22% in dark mode)
        },
      }),
    },

    // Select styling
    Select: {
      defaultProps: {
        radius: tokens.radius.md,
        size: 'sm',
      },
      styles: () => ({
        input: {
          boxShadow: tokens.shadows.formInput,
          border: 'none',
          // 16px prevents iOS Safari zoom on focus (triggered when font-size <= 15px)
          fontSize: '1rem',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
        dropdown: {
          boxShadow: tokens.shadows.dropdown,
          border: 'none',
        },
      }),
    },

    // Checkbox styling
    Checkbox: {
      defaultProps: {
        radius: tokens.radius.sm,
        size: 'sm',
      },
      styles: {
        root: {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },

    // Radio styling
    Radio: {
      defaultProps: {
        size: 'sm',
      },
      styles: {
        root: {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },

    // Switch styling
    Switch: {
      defaultProps: {
        size: 'sm',
      },
      styles: {
        track: {
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        },
      },
    },


    // Alert styling
    Alert: {
      styles: {
        root: {
          border: '1px solid transparent',
          title: {
            fontWeight: tokens.typography.fontWeights.semibold,
          },
          message: {
            color: 'inherit',
          },
        },
      },
    },

    // Notification styling
    Notification: {
      styles: {
        root: {
          border: '1px solid transparent',
          title: {
            fontWeight: tokens.typography.fontWeights.semibold,
          },
          description: {
            color: 'inherit',
          },
        },
      },
    },

    // Drawer styling (global)
    Drawer: {
      defaultProps: {
        overlayProps: { backgroundOpacity: 0.55, blur: 2 },
        size: tokens.containers.drawer,
      },
      styles: (theme: MantineTheme) => ({
        content: {
          backgroundColor: theme.colors.body[6], // Uses virtualColor body - adapts to light/dark
          backdropFilter: 'none',
        },
      }),
    },

    // Tabs - radius from theme, consistent with form/surface components
    Tabs: {
      defaultProps: {
        radius: 'md',
      },
    },

    // Tooltip - radius, ensure readable on both schemes
    Tooltip: {
      defaultProps: {
        radius: 'md',
      },
    },

    // Popover / floating panels - surface-like, shadow and radius
    Popover: {
      defaultProps: {
        radius: 'md',
        shadow: 'md',
      },
      styles: (theme: MantineTheme) => ({
        dropdown: {
          backgroundColor: theme.colors.surface[6],
          borderColor: 'var(--mantine-color-default-border)',
        },
      }),
    },

    // Accordion - radius for panels
    Accordion: {
      defaultProps: {
        radius: 'md',
      },
    },

    // SegmentedControl - consistent radius
    SegmentedControl: {
      defaultProps: {
        radius: 'md',
      },
    },

    // Loader - primary color for brand consistency
    Loader: {
      defaultProps: {
        color: 'primary',
      },
    },
  },
});

export const defaultTheme = mergeMantineTheme(DEFAULT_THEME, themeOverride);

/**
 * Custom CSS variables resolver. Merged with Mantine's default by MantineProvider.
 * Overrides core semantic variables to use our virtualColors so light/dark surfaces
 * and text are consistent without per-component overrides.
 *
 * - body: page/main background (lightBg[6]=white, darkBg[6]=22%)
 * - text: primary text (dark[8]=22% in light, textDark[6]=88% in dark)
 * - default: default surfaces e.g. inputs, default Button (surface-6 = lightBg/darkSurface)
 * - dimmed: secondary text, c="dimmed" (muted-6 = neutral/textDark)
 */
export const cssVariablesResolver = (_theme: MantineTheme) => ({
  variables: {},
  light: {
    '--mantine-color-body': 'var(--mantine-color-body-6)',
    '--mantine-color-text': 'var(--mantine-color-text-8)',
    '--mantine-color-default': 'var(--mantine-color-surface-6)',
    '--mantine-color-dimmed': 'var(--mantine-color-muted-6)',
  },
  dark: {
    '--mantine-color-body': 'var(--mantine-color-body-6)',
    '--mantine-color-text': 'var(--mantine-color-text-6)',
    '--mantine-color-default': 'var(--mantine-color-surface-6)',
    '--mantine-color-dimmed': 'var(--mantine-color-muted-6)',
  },
});
